import { Button, FormControl, Grid, Group, IconButton, Layout, Page } from "@/comn/components";
import { useAuth, useFetch, useForm, useGrid, useModal, usePage, useResource, useStore, useToast } from "@/comn/hooks";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { comnUtils } from "@/comn/utils";
import { useTranslation } from "react-i18next";
import {
    URLS,
    APIS,
    SF_INTF_DOC_HIST_INFO,
    SG_INTF_DOC_HIST_LIST2,
} from "@/tz/go/egaz/osci/ptl/intf/hist/services/OsciDocRcpnMgmtService";
import { OSCI0113003Q } from "@/tz/go/egaz/osci/ptl/intf/hist/OSCI0113003Q";

/*
 * Interface Document Send/Reception Management
 * !== 인터페이스 문서 송/수신 관리 상세정보 ==!
 */
export const OSCI0113002S = () => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const toast = useToast();
    const modal = useModal();
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const [sndrcvDvnCd, setSndrcvDvnCd] = useState();
    const { trscLogId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const [intfDocHistLstData, setIntfDocHistLstData] = useState(comnUtils.getGridData([]));

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0109" }, // 송수신 구분 코드
            { area: "comnCd", comnCd: "OSC0102" }, // 송수신 구분 코드
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        intfDocHist: useForm({
            defaultSchema: SF_INTF_DOC_HIST_INFO,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    const grid = {
        intfDocHistList: useGrid({
            defaultSchema: SG_INTF_DOC_HIST_LIST2,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    const fetch = {
        // Gets Document Reception information. !== 전자문서 수신 정보를 조회한다.==!
        getIntfDocHist: useFetch({
            api: () => APIS.getIntfDocHist(trscLogId),
            enabled: !!trscLogId,
            onSuccess: (data) => {
                const intfDocHist = data?.intfDocHist?.content;
                form.intfDocHist.setValues(intfDocHist);
                console.log(intfDocHist.intfDocResdHistLst);
                setIntfDocHistLstData(comnUtils.getGridData(intfDocHist.intfDocResdHistLst));
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    const { errorMessage } = error.response.data;

                    modal.openModal({
                        content: errorMessage.content,
                        onConfirm: () => {
                            navigate(URLS.osci0113001q);
                        },
                        close: false,
                    });

                    return;
                }

                toast.showToast({ type: "error", content: "msg.osc.00035" }); // An error occurred. !== 에러가 발생했습니다! ==!
            },
        }),
        // Update sending electronic documents !== 전자문서 발신 수정 ==!
        insertIntfDocResd: useFetch({
            api: (data) => APIS.insertIntfDocResd({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00073", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        fetch.getIntfDocHist.fetch();
                        grid.intfDocHistList.setPage(0);
                    },
                    close: false,
                });
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    form.intfDocHist.setErrors(error.response.data.errors);
                    return;
                }

                toast.showToast({ type: "error", content: "msg.osc.00035" }); // An error occurred. !== 에러가 발생했습니다! ==!
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // on Save Button Click Event Handler !== 저장 버튼 클릭 ==!
        onResdBtnClick: () => {
            if (!comnUtils.isEmpty(form.intfDocHist.errors)) {
                toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }

            form.intfDocHist.handleSubmit(
                () => {
                    modal.openModal({
                        content: "msg.osc.00090", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.insertIntfDocResd.fetch(form.intfDocHist.getValues());
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
        getResdHistDtl: (rsndTrscLogId: String) => {
            navigate(`${URLS.osci0113002s}/${rsndTrscLogId}`);
        },
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    // ACK Message 는 폼 컨트롤 대신 직접 렌더하므로 값 변경을 구독한다.
    const rspnMsgCn = form.intfDocHist.watch("rspnMsgCn");

    const render = {
        grid_intfDocHistList: {
            cell: {
                trscLogId: (props: any) => {
                    const { rowValues, value } = props;
                    // return <Link to={`/ptli/elctdoc/rcpn/osci0113002q/${rowValues.trscLogId}`}>{value}</Link>;
                    return (
                        <Button color="blue" variant="underlined" onClick={() => handler.getResdHistDtl(value)}>
                            {value}
                        </Button>
                    );
                },
            },
        },
    };

    /*
     * Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        if (trscLogId) {
            fetch.getIntfDocHist.fetch();
            grid.intfDocHistList.setPage(0);
        }
    }, [trscLogId]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.intfDocHist.schema.trscLogId}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocHist.schema.intfId}></Group.Control>
                            <Group.Control {...form.intfDocHist.schema.intfNm}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocHist.schema.sndrcvDvnCd}></Group.Control>
                            <Group.Control {...form.intfDocHist.schema.orgEnpoId}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocHist.schema.sendOrgId}></Group.Control>
                            <Group.Control {...form.intfDocHist.schema.sendOrgCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocHist.schema.rcveOrgId}></Group.Control>
                            <Group.Control {...form.intfDocHist.schema.rcveOrgCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocHist.schema.frstRgsrDtm}></Group.Control>
                            <Group.Control {...form.intfDocHist.schema.trscStatCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocHist.schema.trmssnFailCn} controlSize={10}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.intfDocHist.schema.trmssnMsgCn}
                                controlSize={10}
                                label={
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            width: "100%",
                                        }}
                                    >
                                        {t("L_MSG_ORGN_CN")}
                                        <Button
                                            role="search"
                                            style={{ display: "inline-block" }}
                                            onClick={() => {
                                                const msg = form.intfDocHist.getValue("trmssnMsgCn");

                                                if (comnUtils.isEmpty(msg)) return;

                                                const modalCallback = () => {
                                                    modal.closeModal();
                                                };

                                                modal.openModal({
                                                    draggable: true,
                                                    size: "md",
                                                    content: <OSCI0113003Q callback={modalCallback} msg={msg} />,
                                                });
                                            }}
                                        >
                                            Beautify
                                        </Button>
                                    </div>
                                }
                            ></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            {/*
                             * 읽기 전용 폼 컨트롤은 내용 폭까지 줄어들어 배정된 칸을 다 쓰지 못한다.
                             * ACK Message 는 내용이 길어 전체 폭이 필요하므로 값을 직접 렌더한다.
                             */}
                            <Group.Col
                                colSize={10}
                                labelSize={2}
                                label={
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            width: "100%",
                                        }}
                                    >
                                        {t("L_MSG_ACK_CN")}
                                        <Button
                                            role="search"
                                            style={{ display: "inline-block" }}
                                            onClick={() => {
                                                const msg = form.intfDocHist.getValue("rspnMsgCn");

                                                if (comnUtils.isEmpty(msg)) return;

                                                const modalCallback = () => {
                                                    modal.closeModal();
                                                };

                                                modal.openModal({
                                                    draggable: true,
                                                    size: "md",
                                                    content: <OSCI0113003Q callback={modalCallback} msg={msg} />,
                                                });
                                            }}
                                        >
                                            Beautify
                                        </Button>
                                    </div>
                                }
                            >
                                <pre className="w-full whitespace-pre-wrap break-all">{rspnMsgCn}</pre>
                            </Group.Col>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Layout>
                        <Layout.Left>
                            <Group.Title title={"Resend History List"} titleSize={2}></Group.Title>
                        </Layout.Left>
                    </Layout>
                    <Group.Section>
                        <Grid
                            {...grid.intfDocHistList.grid}
                            data={intfDocHistLstData}
                            render={render.grid_intfDocHistList}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0113001q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    {!!form.intfDocHist.getValue("trscRqstId") && (
                        <Button
                            role="save"
                            onClick={() => {
                                handler.onResdBtnClick();
                            }}
                        >
                            {t("B_RE_RQST")}
                        </Button>
                    )}
                </Layout.Right>
            </Layout>
        </Page>
    );
};
